import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card } from '@/components/ui/card';
import { Server, Database, HardDrive, Globe, Cloud, Cpu, Container } from 'lucide-react';

interface CloudResourceNodeData {
  id: string;
  label: string;
  category?: string;
  status?: string;
  criticality?: string;
}

interface CloudResourceNodeProps {
  data: CloudResourceNodeData;
  selected: boolean;
}

const getIcon = (category?: string) => {
  switch (category?.toLowerCase()) {
    case 'vm':
    case 'virtual machine':
    case 'compute':
      return <Server className="h-4 w-4" />;
    case 'database':
    case 'sql':
    case 'rds':
      return <Database className="h-4 w-4" />;
    case 'storage':
    case 'blob':
    case 's3':
      return <HardDrive className="h-4 w-4" />;
    case 'cdn':
    case 'networking':
    case 'load balancer':
      return <Globe className="h-4 w-4" />;
    case 'container':
    case 'kubernetes':
    case 'aks':
    case 'eks':
      return <Container className="h-4 w-4" />;
    case 'function':
    case 'lambda':
    case 'serverless':
      return <Cpu className="h-4 w-4" />;
    default:
      return <Cloud className="h-4 w-4" />;
  }
};

export const CloudResourceNode = memo(({ data, selected }: CloudResourceNodeProps) => {
  return (
    <Card className={`min-w-[120px] p-2 bg-purple-50 dark:bg-purple-950/50 border-purple-200 dark:border-purple-800 ${selected ? 'ring-2 ring-purple-500' : ''}`}>
      <div className="flex items-center gap-2">
        <div className="text-purple-600 dark:text-purple-400">
          {getIcon(data.category)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{data.label}</div>
          {data.category && (
            <div className="text-xs text-muted-foreground truncate">{data.category}</div>
          )}
        </div>
      </div>

      <Handle
        id="top"
        type="source"
        position={Position.Top}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-purple-500 bg-background hover:bg-purple-200 dark:hover:bg-purple-800"
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-purple-500 bg-background hover:bg-purple-200 dark:hover:bg-purple-800"
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-purple-500 bg-background hover:bg-purple-200 dark:hover:bg-purple-800"
      />
      <Handle
        id="left"
        type="source"
        position={Position.Left}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-purple-500 bg-background hover:bg-purple-200 dark:hover:bg-purple-800"
      />
    </Card>
  );
});

CloudResourceNode.displayName = 'CloudResourceNode';
